import Heading from "components/Heading/Heading";
import { motion } from "framer-motion";
import React, { FC } from "react";
import NcImage from "shared/NcImage/NcImage";
import HIW1img from "images/HIW1-min.webp";
import HIW2img from "images/HIW2-min.webp";
import HIW3img from "images/HIW3-min.webp";
import VectorImg from "images/VectorHIW.svg";
import { fadeInUp, scaleIn, staggerContainer } from "utils/motionPresets";

export interface SectionHowItWorkProps {
  className?: string;
  data?: {
    id: number;
    title: string;
    desc: string;
    img: string;
    imgDark?: string;
  }[];
}

const DEMO_DATA: SectionHowItWorkProps["data"] = [
  {
    id: 1,
    img: HIW1img,
    title: "Find Your Product",
    desc: "Browse our extensive catalog to discover high-quality construction solutions tailored to your project needs.",
  },
  {
    id: 2,
    img: HIW2img,
    title: "Check Specs & Details",
    desc: "Explore detailed specs, materials, and features to ensure the perfect fit for your requirements.",
  },
  {
    id: 3,
    img: HIW3img,
    title: "Get in Touch",
    desc: "Contact our team for expert advice, custom orders, or bulk purchases — we're here to help!",
  },
];

const SectionHowItWork: FC<SectionHowItWorkProps> = ({
  className = "",
  data = DEMO_DATA,
}) => {
  return (
    <div
      className={`nc-SectionHowItWork ${className}`}
      data-nc-id="SectionHowItWork"
    >
      <Heading isCenter desc="Three simple steps to get the right building solution">
        How it works
      </Heading>
      <motion.div
        className="mt-16 relative grid md:grid-cols-3 gap-12 lg:gap-20"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <img
          className="hidden md:block absolute inset-x-0 top-10 opacity-60"
          src={VectorImg}
          alt=""
        />
        {data.map((item, index) => (
          <motion.div
            key={item.id}
            variants={scaleIn}
            className="relative flex flex-col items-center max-w-xs mx-auto group"
          >
            <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center font-bold text-lg shadow-bcr z-10">
              {index + 1}
            </div>
            {item.imgDark ? (
              <>
                <NcImage
                  containerClassName="dark:hidden block mb-8 max-w-[200px] mx-auto group-hover:scale-105 transition-transform duration-300"
                  src={item.img}
                />
                <NcImage
                  containerClassName="hidden dark:block mb-8 max-w-[200px] mx-auto group-hover:scale-105 transition-transform duration-300"
                  src={item.imgDark}
                />
              </>
            ) : (
              <NcImage
                containerClassName="mb-6 w-28 h-28 sm:w-36 sm:h-36 mx-auto rounded-full overflow-hidden border-2 border-primary-200 dark:border-primary-700 group-hover:border-primary-500 transition-colors duration-300 shadow-bcr"
                src={item.img}
              />
            )}
            <div className="text-center mt-auto">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">{item.title}</h2>
              <span className="block mt-4 text-neutral-500 dark:text-neutral-400 leading-relaxed">
                {item.desc}
              </span>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default SectionHowItWork;
